import * as O from 'fp-ts/Option';
import * as S from 'fp-ts/string';
import { constant, flow, pipe } from 'fp-ts/function';

import { RecipeProduct } from '@domains/recipes';

export const getMinWidthMedia = (value: number, unit = 'px'): string =>
    `(min-width: ${value}${unit})`;

export const pluralize = (count: number, words: string[]): string => {
    const cases = [2, 0, 1, 1, 1, 2];
    return words[count % 100 > 4 && count % 100 < 20 ? 2 : cases[Math.min(count % 10, 5)]];
};

export const formatCookingTime = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const minutesRemainder = Math.floor(minutes % 60);

    const hoursWord = pluralize(hours, ['час', 'часа', 'часов']);
    const minutesRemainderWord = pluralize(minutesRemainder, ['минута', 'минуты', 'минут']);

    if (hours === 0) {
        return `${minutesRemainder} ${minutesRemainderWord}`;
    }

    if (hours > 0 && minutesRemainder === 0) {
        return `${hours} ${hoursWord}`;
    }

    return `${hours} ${hoursWord} ${minutesRemainder} ${minutesRemainderWord}`;
};

export const getProductsTotalPrice = (recipeProducts: RecipeProduct[]): FeipApi.Kopecks => {
    return recipeProducts.reduce((sum, recipeProduct) => {
        const { product, quantity } = recipeProduct;
        const total = quantity * product.purchase_unit.price_in_kopecks.new;
        return (sum + total) as FeipApi.Kopecks;
    }, 0 as FeipApi.Kopecks);
};

export const formatNumberOrIngredients = (number: number): string =>
    `${number} ${pluralize(number, ['ингредиент', 'ингредиента', 'ингредиентов'])}`;

export const onlyNumbers = (value: string): string => value.replace(/\D/g, '');

export const roundNumber = (value: number, fractionDigits = 1): number =>
    Number(value.toFixed(value % 1 === 0 ? 0 : fractionDigits));

export const pixelsToNumber = (numberOfPixels: string | undefined): number =>
    pipe(
        numberOfPixels,
        O.fromNullable,
        O.filter(S.endsWith('px')),
        O.map(flow(S.slice(0, -2), Number.parseInt)),
        O.filter((number: number) => Number.isFinite(number) && number > 0),
        O.getOrElse(constant(0)),
    );

export const getSafeURL = (url: string) => encodeURI(url);

export const upperCaseFirstCart = (text: string): string =>
    `${text.slice(0, 1).toUpperCase()}${text.slice(1, -1)}`;

export const replaceSpecialCharacters = (value: string, replaceValue = ' ') =>
    value.replace(/<br\s*?\/?>|\\n|\\r\\n|\\n\\r/g, replaceValue);
